import { PaginationType } from '../../types/utils/pagination.type';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import {
  HeartbeatChangeType,
  HeartbeatStatus,
  ULARECentralStationType,
  ULARELine,
} from '../../types/ulare/ulare.types';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { createSlice } from '@reduxjs/toolkit';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import {
  cancelAlarm,
  deleteAllow,
  deleteCentralStation,
  deleteLine,
  getAllowList,
  getCentralStation,
  getCentralStationsList,
  getConnection,
  getHeartbeatChanges,
  getHeartbeatStatus,
  getLineIDs,
  getULAREAlarmsList,
  patchCentralStation,
  postAllow,
  postCentralStation,
  postLine,
  resetConnection,
} from './ulare.action';
import snackbarUtils from '../../utils/SnackbarUtils';
import { AlarmType } from '../../types/alarm/alarm.type';

export type CentralStationsRouteParams = RouteParamsType;

export interface ULAREState {
  centralStationsList: PaginationType<ULARECentralStationType> | null;
  centralStationsListFetchStatus: FETCH_STATUS;
  centralStation: ULARECentralStationType | null;
  allowList: string[] | null;
  allowListFetchStatus: FETCH_STATUS;
  connection: { connectedAt: Date | null; ipAddress: string } | null;
  connectionFetchStatus: FETCH_STATUS;
  lineIDs: ULARELine[] | null;
  lineIDsFetchStatus: FETCH_STATUS;
  centralStationFetchStatus: FETCH_STATUS;
  centralStationPostStatus: FETCH_STATUS;
  centralStationPatchStatus: FETCH_STATUS;
  centralStationDeleteStatus: FETCH_STATUS;
  connectionDeleteStatus: FETCH_STATUS;
  centralStationsRouteParams: CentralStationsRouteParams;
  allowPostStatus: FETCH_STATUS;
  linePostStatus: FETCH_STATUS;
  allowDeleteStatus: FETCH_STATUS;
  lineDeleteStatus: FETCH_STATUS;
  heartbeatStatus: null | HeartbeatStatus;
  heartbeatChangesList: PaginationType<HeartbeatChangeType> | null;
  heartbeatChangesListFetchStatus: FETCH_STATUS;
  // ALARMS
  alarmsRouteParams: RouteParamsType;
  alarmsList: null | PaginationType<AlarmType>;
  alarmsListFetchStatus: FETCH_STATUS;
  cancelAlarmPatchStatus: FETCH_STATUS;
}

export const initialCentralStationsRouteParams: CentralStationsRouteParams = {
  page: 1,
  limit: 10,
  order: 'obj.csId',
  q: '',
};

export const initialAlarmsRouteParams = {
  page: 1,
  limit: 10,
  order: '-obj.id',
  q: '',
};

const INIT_STATE: ULAREState = {
  centralStationsList: null,
  centralStationsListFetchStatus: FETCH_STATUS.NULL,
  centralStation: null,
  allowList: null,
  allowListFetchStatus: FETCH_STATUS.NULL,
  connectionFetchStatus: FETCH_STATUS.NULL,
  connection: null,
  lineIDs: null,
  lineIDsFetchStatus: FETCH_STATUS.NULL,
  centralStationFetchStatus: FETCH_STATUS.NULL,
  centralStationPostStatus: FETCH_STATUS.NULL,
  centralStationPatchStatus: FETCH_STATUS.NULL,
  centralStationDeleteStatus: FETCH_STATUS.NULL,
  connectionDeleteStatus: FETCH_STATUS.NULL,
  centralStationsRouteParams: initialCentralStationsRouteParams,
  allowPostStatus: FETCH_STATUS.NULL,
  linePostStatus: FETCH_STATUS.NULL,
  allowDeleteStatus: FETCH_STATUS.NULL,
  lineDeleteStatus: FETCH_STATUS.NULL,
  heartbeatStatus: null,
  heartbeatChangesList: null,
  heartbeatChangesListFetchStatus: FETCH_STATUS.NULL,
  // ALARMS
  alarmsRouteParams: initialAlarmsRouteParams,
  alarmsList: null,
  alarmsListFetchStatus: FETCH_STATUS.NULL,
  cancelAlarmPatchStatus: FETCH_STATUS.NULL,
};

export const ulareSlice = createSlice({
  name: 'ulare',
  initialState: INIT_STATE,
  reducers: {
    setHeartbeatChangesList: (state, action) => {
      state.heartbeatChangesList = action.payload;
    },
    setHeartbeatChangesListFetchStatus: (state, action) => {
      state.heartbeatChangesListFetchStatus = action.payload;
    },
    setCentralStation: (state, action) => {
      state.centralStation = action.payload;
    },
    setAllowPostStatus: (state, action) => {
      state.allowPostStatus = action.payload;
    },
    setLinePostStatus: (state, action) => {
      state.linePostStatus = action.payload;
    },
    setAllowDeleteStatus: (state, action) => {
      state.allowDeleteStatus = action.payload;
    },
    setLineDeleteStatus: (state, action) => {
      state.lineDeleteStatus = action.payload;
    },
    setCentralStationFetchStatus: (state, action) => {
      state.centralStationFetchStatus = action.payload;
    },
    setAllowList: (state, action) => {
      state.allowList = action.payload;
    },
    setAllowListFetchStatus: (state, action) => {
      state.allowListFetchStatus = action.payload;
    },
    setConnection: (state, action) => {
      state.connection = action.payload;
    },
    setConnectionFetchStatus: (state, action) => {
      state.connectionFetchStatus = action.payload;
    },
    setLineIDs: (state, action) => {
      state.lineIDs = action.payload;
    },
    setLineIDsFetchStatus: (state, action) => {
      state.lineIDsFetchStatus = action.payload;
    },
    setCentralStationPostStatus: (state, action) => {
      state.centralStationPostStatus = action.payload;
    },
    setCentralStationPatchStatus: (state, action) => {
      state.centralStationPatchStatus = action.payload;
    },
    setCentralStationDeleteStatus: (state, action) => {
      state.centralStationDeleteStatus = action.payload;
    },
    setConnectionDeleteStatus: (state, action) => {
      state.connectionDeleteStatus = action.payload;
    },
    setCentralStationsList: (state, action) => {
      state.centralStationsList = action.payload;
    },
    setCentralStationsListFetchStatus: (state, action) => {
      state.centralStationsListFetchStatus = action.payload;
    },
    setHeartbeatStatus: (state, action) => {
      state.heartbeatStatus = action.payload;
    },
    setAlarmsList: (state, action) => {
      state.alarmsList = action.payload;
    },
    setAlarmsListFetchStatus: (state, action) => {
      state.alarmsListFetchStatus = action.payload;
    },
    setCancelAlarmPatchStatus: (state, action) => {
      state.cancelAlarmPatchStatus = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(SET_ROUTE_PARAMS, (state, action) => {
      const { payload } = action as ToolkitActionType<{ reducer: string; data: RouteParamsType }>;
      const data = { ...payload.data };
      data.limit = +payload?.data?.limit;

      if (payload.reducer === 'centralStations') {
        state.centralStationsRouteParams = { ...state.centralStationsRouteParams, ...data };
      }

      if (payload.reducer === 'ulare-alarms') {
        state.alarmsRouteParams = { ...state.alarmsRouteParams, ...payload.data };
      }
    });

    // POST CENTRAL STATION DETAILS
    builder.addCase(postCentralStation.pending, state => {
      state.centralStationPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postCentralStation.rejected, (state, action) => {
      state.centralStationPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postCentralStation.fulfilled, state => {
      state.centralStationPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Central Station created successfully');
      });
    });

    // GET CENTRAL STATIONS LIST
    builder.addCase(getCentralStationsList.pending, state => {
      state.centralStationsListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getCentralStationsList.rejected, (state, action) => {
      if (action.payload) {
        state.centralStationsListFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getCentralStationsList.fulfilled, (state, action) => {
      state.centralStationsListFetchStatus = FETCH_STATUS.FULFILLED;
      state.centralStationsList = action.payload;
    });

    // GET CENTRAL STATION DETAILS
    builder.addCase(getCentralStation.pending, state => {
      state.centralStationFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getCentralStation.rejected, (state, action) => {
      state.centralStationFetchStatus = FETCH_STATUS.REJECTED;
      state.centralStation = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getCentralStation.fulfilled, (state, action) => {
      state.centralStationFetchStatus = FETCH_STATUS.FULFILLED;
      state.centralStation = action.payload;
    });

    // GET CENTRAL STATION CONNECTION
    builder.addCase(getConnection.pending, state => {
      state.connectionFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getConnection.rejected, (state, action) => {
      state.connectionFetchStatus = FETCH_STATUS.REJECTED;
      state.connection = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getConnection.fulfilled, (state, action) => {
      state.connectionFetchStatus = FETCH_STATUS.FULFILLED;
      state.connection = action.payload;
    });

    // GET HEARTBEAT STATUS
    builder.addCase(getHeartbeatStatus.pending, state => {
      state.heartbeatStatus = null;
    });
    builder.addCase(getHeartbeatStatus.rejected, (state, action) => {
      state.heartbeatStatus = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getHeartbeatStatus.fulfilled, (state, action) => {
      state.heartbeatStatus = action.payload;
    });

    // GET HEARTBEAT CHANGES LIST
    builder.addCase(getHeartbeatChanges.pending, state => {
      state.heartbeatChangesListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getHeartbeatChanges.rejected, (state, action) => {
      state.heartbeatChangesListFetchStatus = FETCH_STATUS.REJECTED;
      state.heartbeatChangesList = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getHeartbeatChanges.fulfilled, (state, action) => {
      state.heartbeatChangesListFetchStatus = FETCH_STATUS.FULFILLED;
      state.heartbeatChangesList = action.payload;
    });

    // GET ALLOW LIST
    builder.addCase(getAllowList.pending, state => {
      state.allowListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getAllowList.rejected, (state, action) => {
      state.allowListFetchStatus = FETCH_STATUS.REJECTED;
      state.allowList = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getAllowList.fulfilled, (state, action) => {
      state.allowListFetchStatus = FETCH_STATUS.FULFILLED;
      state.allowList = action.payload;
    });

    // GET LINE IDs
    builder.addCase(getLineIDs.pending, state => {
      state.lineIDsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getLineIDs.rejected, (state, action) => {
      state.lineIDsFetchStatus = FETCH_STATUS.REJECTED;
      state.lineIDs = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getLineIDs.fulfilled, (state, action) => {
      state.lineIDsFetchStatus = FETCH_STATUS.FULFILLED;
      state.lineIDs = action.payload;
    });

    // PATCH CENTRAL STATION DETAILS
    builder.addCase(patchCentralStation.pending, state => {
      state.centralStationPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchCentralStation.rejected, (state, action) => {
      state.centralStationPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(patchCentralStation.fulfilled, state => {
      state.centralStationPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Central Station updated successfully');
      });
    });

    // DELETE CENTRAL STATION DETAILS
    builder.addCase(deleteCentralStation.pending, state => {
      state.centralStationDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteCentralStation.rejected, (state, action) => {
      state.centralStationDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteCentralStation.fulfilled, state => {
      state.centralStationDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Central Station deleted successfully');
      });
    });

    // DELETE CENTRAL STATION CONNECTION
    builder.addCase(resetConnection.pending, state => {
      state.connectionDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(resetConnection.rejected, (state, action) => {
      state.connectionDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(resetConnection.fulfilled, state => {
      state.connectionDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('The connection has been restarted');
      });
    });

    // POST ALLOW
    builder.addCase(postAllow.pending, state => {
      state.allowPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postAllow.rejected, (state, action) => {
      state.allowPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postAllow.fulfilled, state => {
      state.allowPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Address added successfully');
      });
    });

    // POST LINE
    builder.addCase(postLine.pending, state => {
      state.linePostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postLine.rejected, (state, action) => {
      state.linePostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postLine.fulfilled, state => {
      state.linePostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Line created successfully');
      });
    });

    // DELETE ALLOW
    builder.addCase(deleteAllow.pending, state => {
      state.allowDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteAllow.rejected, (state, action) => {
      state.allowDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteAllow.fulfilled, state => {
      state.allowDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Address deleted successfully');
      });
    });

    // DELETE LINE
    builder.addCase(deleteLine.pending, state => {
      state.lineDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteLine.rejected, (state, action) => {
      state.lineDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteLine.fulfilled, state => {
      state.lineDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Line deleted successfully');
      });
    });

    builder.addCase(getULAREAlarmsList.pending, state => {
      state.alarmsListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getULAREAlarmsList.rejected, (state, action) => {
      state.alarmsListFetchStatus = FETCH_STATUS.REJECTED;
      state.alarmsList = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getULAREAlarmsList.fulfilled, (state, action) => {
      state.alarmsListFetchStatus = FETCH_STATUS.FULFILLED;
      state.alarmsList = action.payload;
    });

    builder.addCase(cancelAlarm.pending, state => {
      state.cancelAlarmPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(cancelAlarm.rejected, (state, action) => {
      state.cancelAlarmPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(cancelAlarm.fulfilled, (state, action) => {
      state.cancelAlarmPatchStatus = FETCH_STATUS.FULFILLED;
    });
  },
});

export const {
  setCentralStation,
  setCentralStationsList,
  setCentralStationsListFetchStatus,
  setCentralStationPostStatus,
  setCentralStationPatchStatus,
  setCentralStationFetchStatus,
  setAllowListFetchStatus,
  setLineIDs,
  setLineIDsFetchStatus,
  setAllowList,
  setLinePostStatus,
  setAllowPostStatus,
  setAllowDeleteStatus,
  setLineDeleteStatus,
  setHeartbeatStatus,
  setHeartbeatChangesListFetchStatus,
  setHeartbeatChangesList,
  setCentralStationDeleteStatus,
  setConnectionFetchStatus,
  setConnection,
  setConnectionDeleteStatus,
  setAlarmsList,
  setAlarmsListFetchStatus,
  setCancelAlarmPatchStatus,
} = ulareSlice.actions;

export default ulareSlice.reducer;
